<template>
  <router-link
    v-if="showIndicator"
    :to="{ name: 'admin-onboarding-checklist' }"
    class="onboarding-progress"
    title="Onboarding Progress"
  >
    <svg class="onboarding-progress__circle" viewBox="0 0 36 36">
      <circle class="onboarding-progress__track" cx="18" cy="18" r="15.5" />
      <circle
        class="onboarding-progress__fill"
        cx="18"
        cy="18"
        r="15.5"
        :style="{ strokeDashoffset: strokeDashoffset }"
      />
    </svg>
    <span class="onboarding-progress__text">{{ progressPercentage }}</span>
  </router-link>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useOnboardingChecklist } from '@/store/onboarding-checklist';

const onboardingStore = useOnboardingChecklist();

// These are already computed refs from the store
const progressPercentage = onboardingStore.progressPercentage;
const totalItems = onboardingStore.totalItems;

// Only show if onboarding exists and is not complete (progress < 100)
const showIndicator = computed(() => {
  const progress = progressPercentage.value;
  return totalItems.value > 0 && progress < 100;
});

// Calculate stroke dashoffset for circular progress
// Circle circumference = 2πr where r = 15.5
const circumference = 2 * Math.PI * 15.5;
const strokeDashoffset = computed(() => {
  return circumference - (progressPercentage.value / 100) * circumference;
});
</script>

<style scoped lang="scss">
@import '@styles/variables';

.onboarding-progress {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  // Matches the bell/chat buttons next to it: same 4.2rem circle, same
  // shrink-proof sizing, so the row reads as one set of controls.
  width: 4.2rem;
  height: 4.2rem;
  flex-shrink: 0;
  box-sizing: border-box;
  cursor: pointer;
  border-radius: 50%;
  background: rgba($c-ink, 0);
  transition:
    background $transition-smooth $ease-in-out-smooth,
    transform $transition-fast $ease-out-soft;

  &:hover {
    background: rgba($c-ink, 0.06);
  }

  &:active {
    transform: scale(0.92);
  }

  &__circle {
    width: 3rem;
    height: 3rem;
    transform: rotate(-90deg); // Start from top
  }

  &__track {
    fill: none;
    stroke: rgba($c-ink, 0.15);
    stroke-width: 2;
  }

  &__fill {
    fill: none;
    stroke: $c-accent-deep;
    stroke-width: 2;
    stroke-dasharray: #{2 * 3.14159 * 15.5};
    stroke-linecap: round;
    transition: stroke-dashoffset 0.3s ease;
  }

  &__text {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -48%);
    font-size: 1rem;
    font-weight: 600;
    color: $c-ink;
  }
}
</style>
